/**
 * Data layer for `data-visibility-toggle-assigned-users-only-vs-all`. Thin typed fetch wrappers over /api/v1/data-visibility-toggle-assigned-users-only-vs-all.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface DataVisibilityToggleAssignedUsersOnlyVsAllRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListDataVisibilityToggleAssignedUsersOnlyVsAllResponse {
	count: number;
	items: DataVisibilityToggleAssignedUsersOnlyVsAllRow[];
}

export interface CreateDataVisibilityToggleAssignedUsersOnlyVsAllBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/data-visibility-toggle-assigned-users-only-vs-all";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListDataVisibilityToggleAssignedUsersOnlyVsAllResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListDataVisibilityToggleAssignedUsersOnlyVsAllResponse>(res);
}

export async function create(body: CreateDataVisibilityToggleAssignedUsersOnlyVsAllBody): Promise<DataVisibilityToggleAssignedUsersOnlyVsAllRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<DataVisibilityToggleAssignedUsersOnlyVsAllRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const dataVisibilityToggleAssignedUsersOnlyVsAllQueryKeys = {
	all: ["data-visibility-toggle-assigned-users-only-vs-all"] as const,
	list: () => [...dataVisibilityToggleAssignedUsersOnlyVsAllQueryKeys.all] as const,
	detail: (id: string) => [...dataVisibilityToggleAssignedUsersOnlyVsAllQueryKeys.all, id] as const,
};
